import { useMemo, useState } from 'react';

import { type QuizBankProblem } from '../types';

interface UseQuizBankSelectionParams {
  /** 이미 빌드에 담긴 문제 — 다시 선택되지 않는다 */
  addedProblemIds: string[];
  /** 더 담을 수 있는 개수. `undefined` 면 제한 없음 */
  maxSelectableCount?: number;
}

/**
 * 문제 다중 선택. 상한은 빌드 폼이 정하므로 남은 자리 수를 밖에서 받고,
 * 상한에 닿으면 미선택 카드를 비활성으로 만든다. 담기 콜백이 문항 본문을
 * 그대로 넘겨야 해서 id 가 아니라 객체를 들고 있는다.
 */
export const useQuizBankSelection = ({
  addedProblemIds,
  maxSelectableCount,
}: UseQuizBankSelectionParams) => {
  const [selectedProblems, setSelectedProblems] = useState<QuizBankProblem[]>(
    [],
  );

  const selectedIds = useMemo(
    () => selectedProblems.map(problem => problem.id),
    [selectedProblems],
  );

  const isFull =
    maxSelectableCount !== undefined &&
    selectedProblems.length >= maxSelectableCount;

  const toggle = (problem: QuizBankProblem) => {
    if (addedProblemIds.includes(problem.id)) return;

    setSelectedProblems(prev => {
      const alreadySelected = prev.some(selected => selected.id === problem.id);
      if (alreadySelected) {
        return prev.filter(selected => selected.id !== problem.id);
      }
      // 해제는 언제나 가능하고, 추가만 상한에 걸린다.
      if (
        maxSelectableCount !== undefined &&
        prev.length >= maxSelectableCount
      ) {
        return prev;
      }
      return [...prev, problem];
    });
  };

  const clear = () => setSelectedProblems([]);

  return { selectedProblems, selectedIds, isFull, toggle, clear };
};
